import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import type { RoundState, SessionState, ServerErrorPayload } from '@ember/shared';
import { getSocket } from '@/lib/socket';

export type ConnectionStatus = 'connected' | 'reconnecting' | 'disconnected';

interface UseSessionSocketResult {
  session: SessionState | null;
  /** Round actuellement vise par le curseur LOCAL du joueur (peut differer du round serveur). */
  round: RoundState | null;
  /** Liste complete de tous les rounds de la session avec leurs etats / reponses. */
  rounds: RoundState[];
  /** Position 1-indexed du curseur local. Si > totalRounds, le joueur est passe au recap. */
  currentRoundOrder: number;
  /** Nombre total de rounds prevus (10 au MVP). 0 tant que la liste n'est pas chargee. */
  totalRounds: number;
  /** True quand le joueur a depasse le dernier round (curseur > total) -> afficher EndCard. */
  hasFinishedLocally: boolean;
  loading: boolean;
  error: ServerErrorPayload | null;
  /** Etat de la connexion Socket.IO, expose pour la banniere de reconnexion. */
  connectionStatus: ConnectionStatus;
  /** Demande au serveur de lancer la partie (status WAITING -> ACTIVE). */
  startSession: () => void;
  /**
   * Soumet la reponse du joueur au round courant.
   * En mode GUESS, `guessText` contient ce que le joueur devine que l'autre
   * va repondre. Ignore en mode STANDARD (cote serveur).
   *
   * `mediaFilename` + `mediaType` (optionnels) : pour les Dare physiques.
   * Le fichier doit avoir ete uploade via `api.uploadMedia` au prealable,
   * et le filename retourne est passe ici pour etre lie a la Response.
   */
  submitAnswer: (
    roundId: string,
    playerId: string,
    text: string,
    guessText?: string,
    mediaFilename?: string,
    mediaType?: 'PHOTO' | 'AUDIO',
  ) => void;
  /**
   * Avance le curseur LOCAL d'1 round. Aucun event serveur emis : chaque joueur
   * progresse a son rythme apres le reveal. Si on depasse le dernier round,
   * `hasFinishedLocally` passe a true et l'UI bascule sur la EndCard.
   */
  nextRound: () => void;
}

/** Cle localStorage du curseur local, scoped par session. */
function cursorKey(shortCode: string): string {
  return `ember.session.${shortCode}.cursor`;
}

function readCursor(shortCode: string | undefined): number {
  if (!shortCode || typeof window === 'undefined') return 1;
  const raw = window.localStorage.getItem(cursorKey(shortCode));
  const parsed = raw ? Number.parseInt(raw, 10) : 1;
  return Number.isFinite(parsed) && parsed >= 1 ? parsed : 1;
}

function writeCursor(shortCode: string, cursor: number): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(cursorKey(shortCode), String(cursor));
}

/**
 * Calcule la valeur de `myAnswerText` a appliquer sur un round reçu, en :
 *   1. respectant l'annotation serveur si fournie (`incoming.myAnswerText`)
 *   2. derivant depuis `incoming.answers` si REVEALED (notre reponse y est)
 *   3. preservant l'annotation precedente si presente (cas broadcast strippe)
 *
 * Retourne le round potentiellement enrichi. Pure function pour tests faciles.
 */
function enrichWithMyAnswer(
  incoming: RoundState,
  existing: RoundState | undefined,
  playerId: string | null,
): RoundState {
  if (incoming.myAnswerText !== undefined) return incoming;
  if (incoming.status === 'REVEALED' && playerId) {
    const mine = incoming.answers.find((a) => a.playerId === playerId);
    if (mine) {
      return { ...incoming, myAnswerText: mine.text };
    }
  }
  if (existing?.myAnswerText !== undefined) {
    return { ...incoming, myAnswerText: existing.myAnswerText };
  }
  return incoming;
}

/**
 * Hook qui abonne un composant aux updates Socket.IO d'une session (lobby + jeu).
 *
 * Architecture "curseur local" :
 *   - le serveur stocke tous les rounds et leurs reponses
 *   - chaque client a un curseur LOCAL (persiste en localStorage scoped) qui pointe
 *     sur le round qu'il regarde actuellement
 *   - `nextRound()` n'emet AUCUN event serveur, c'est juste setCursor(prev + 1)
 *   - quand le curseur depasse le total, hasFinishedLocally devient true et le
 *     play-page affiche la EndCard avec le recap complet
 *   - cela permet a chaque joueur de prendre son temps pour lire les reponses
 *     reveles sans bloquer l'autre
 *
 * Annotation viewer :
 *   - si `playerId` est fourni, on le passe au serveur dans `session:subscribe`
 *   - le serveur annote chaque RoundState renvoye avec `myAnswerText` (string
 *     si j'ai repondu, null sinon)
 *   - le client peut ainsi detecter "j'ai deja repondu" meme apres reload et
 *     basculer l'UI sans re-afficher la textarea
 *
 * Cycle de vie :
 *   - mount : connecte le socket, emit `session:subscribe` (avec playerId si dispo)
 *   - sur reconnect Socket.IO : re-emit automatiquement `session:subscribe`
 *   - unmount : emit `session:unsubscribe`, retire les listeners
 */
export function useSessionSocket(
  shortCode: string | undefined,
  playerId?: string | null,
): UseSessionSocketResult {
  const [session, setSession] = useState<SessionState | null>(null);
  const [rounds, setRounds] = useState<RoundState[]>([]);
  const [serverRound, setServerRound] = useState<RoundState | null>(null);
  const [currentRoundOrder, setCurrentRoundOrder] = useState<number>(() => readCursor(shortCode));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ServerErrorPayload | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('disconnected');

  // Ref pour acceder au curseur courant depuis les handlers socket sans
  // ajouter de deps qui retriggerent le useEffect.
  const cursorRef = useRef(currentRoundOrder);
  cursorRef.current = currentRoundOrder;

  // Persiste le curseur a chaque changement.
  useEffect(() => {
    if (shortCode) writeCursor(shortCode, currentRoundOrder);
  }, [shortCode, currentRoundOrder]);

  // Re-sync le curseur quand on change de shortCode (cas tres rare : nav entre 2 sessions).
  useEffect(() => {
    setCurrentRoundOrder(readCursor(shortCode));
  }, [shortCode]);

  useEffect(() => {
    if (!shortCode) {
      setLoading(false);
      return;
    }

    const socket = getSocket();

    /**
     * Merge un round mis a jour dans la liste : on remplace par id si present,
     * sinon on append (ne devrait pas arriver mais defensif).
     *
     * Annotation viewer (`myAnswerText`) : si l'incoming ne l'a pas (cas broadcast
     * round:revealed strippe) mais qu'on connait le playerId :
     *   - REVEALED : on derive depuis `answers` (notre reponse y est presente)
     *   - sinon : on preserve l'annotation existante (si on l'avait du subscribe
     *     initial ou d'un round:updated cible)
     */
    const mergeRound = (incoming: RoundState) => {
      setRounds((prev) => {
        const idx = prev.findIndex((r) => r.id === incoming.id);
        const enriched = enrichWithMyAnswer(incoming, prev[idx], playerId ?? null);
        if (idx < 0) {
          const next = [...prev, enriched];
          next.sort((a, b) => a.order - b.order);
          return next;
        }
        const next = [...prev];
        next[idx] = enriched;
        return next;
      });
    };

    const handleSubscribed = (payload: {
      session: SessionState;
      currentRound: RoundState | null;
      rounds: RoundState[];
    }) => {
      setSession(payload.session);
      setServerRound(payload.currentRound);
      setRounds(payload.rounds);
      setLoading(false);
      setError(null);

      // Auto-clamp du curseur : si le localStorage contient une valeur > total,
      // on ramene au max. Si la partie n'est pas commencee, on reset a 1.
      const total = payload.rounds.length;
      if (total > 0 && cursorRef.current > total + 1) {
        setCurrentRoundOrder(total + 1); // > total = FINISHED, on garde au-dela
      }
    };

    const handleSessionUpdated = (payload: { session: SessionState }) => {
      setSession(payload.session);
    };
    const handleSessionStarted = (payload: { session: SessionState }) => {
      setSession(payload.session);
      // Au start, on remet le curseur a 1 (sinon un joueur qui avait fini une
      // ancienne partie sur le meme shortCode aurait son curseur > total).
      setCurrentRoundOrder(1);
    };
    const handleSessionEnded = (payload: { session: SessionState }) => {
      setSession(payload.session);
      setServerRound(null);
    };
    const handleRoundStart = (payload: { round: RoundState }) => {
      setServerRound(payload.round);
      mergeRound(payload.round);
    };
    const handleRoundUpdated = (payload: { round: RoundState }) => {
      setServerRound(payload.round);
      mergeRound(payload.round);
    };
    const handleRoundRevealed = (payload: { round: RoundState }) => {
      setServerRound(payload.round);
      mergeRound(payload.round);
    };
    const handleRoundsList = (payload: { rounds: RoundState[] }) => {
      setRounds(payload.rounds);
    };
    const handleError = (payload: ServerErrorPayload) => {
      setError(payload);
      setLoading(false);
    };

    // --- Lifecycle de connexion ---------------------------------------------

    const handleConnect = () => {
      setConnectionStatus('connected');
      socket.emit('session:subscribe', {
        shortCode,
        ...(playerId ? { playerId } : {}),
      });
    };
    const handleDisconnect = () => {
      setConnectionStatus('reconnecting');
    };
    const handleReconnectAttempt = () => {
      setConnectionStatus('reconnecting');
    };
    const handleConnectError = () => {
      setConnectionStatus('reconnecting');
    };

    socket.on('session:subscribed', handleSubscribed);
    socket.on('session:updated', handleSessionUpdated);
    socket.on('session:started', handleSessionStarted);
    socket.on('session:ended', handleSessionEnded);
    socket.on('round:start', handleRoundStart);
    socket.on('round:updated', handleRoundUpdated);
    socket.on('round:revealed', handleRoundRevealed);
    socket.on('rounds:list', handleRoundsList);
    socket.on('error', handleError);

    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    socket.io.on('reconnect_attempt', handleReconnectAttempt);
    socket.io.on('error', handleConnectError);

    if (!socket.connected) {
      setConnectionStatus('reconnecting');
      socket.connect();
    } else {
      setConnectionStatus('connected');
    }
    socket.emit('session:subscribe', {
      shortCode,
      ...(playerId ? { playerId } : {}),
    });

    return () => {
      socket.emit('session:unsubscribe', { shortCode });
      socket.off('session:subscribed', handleSubscribed);
      socket.off('session:updated', handleSessionUpdated);
      socket.off('session:started', handleSessionStarted);
      socket.off('session:ended', handleSessionEnded);
      socket.off('round:start', handleRoundStart);
      socket.off('round:updated', handleRoundUpdated);
      socket.off('round:revealed', handleRoundRevealed);
      socket.off('rounds:list', handleRoundsList);
      socket.off('error', handleError);
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
      socket.io.off('reconnect_attempt', handleReconnectAttempt);
      socket.io.off('error', handleConnectError);
    };
    // playerId est inclus pour re-emettre subscribe (annote myAnswerText) quand
    // useSessionPlayer le resout apres le mount initial.
  }, [shortCode, playerId]);

  const totalRounds = rounds.length;

  // Round vise par le curseur local. Si la liste n'est pas chargee, fallback
  // sur le round serveur (pour ne pas afficher d'ecran vide pendant la transition).
  const round = useMemo<RoundState | null>(() => {
    if (totalRounds === 0) return serverRound;
    const target = rounds.find((r) => r.order === currentRoundOrder);
    return target ?? serverRound;
  }, [rounds, currentRoundOrder, serverRound, totalRounds]);

  const hasFinishedLocally = totalRounds > 0 && currentRoundOrder > totalRounds;

  const startSession = useCallback(() => {
    if (!shortCode) return;
    getSocket().emit('session:start', { shortCode });
  }, [shortCode]);

  const submitAnswer = useCallback(
    (
      roundId: string,
      playerId: string,
      text: string,
      guessText?: string,
      mediaFilename?: string,
      mediaType?: 'PHOTO' | 'AUDIO',
    ) => {
      if (!shortCode) return;
      getSocket().emit('round:submit', {
        shortCode,
        roundId,
        playerId,
        text,
        ...(typeof guessText === 'string' && guessText.length > 0 ? { guessText } : {}),
        ...(mediaFilename ? { mediaFilename } : {}),
        ...(mediaType ? { mediaType } : {}),
      });
    },
    [shortCode],
  );

  const nextRound = useCallback(() => {
    // Pas d'event serveur : le curseur est purement local. Chaque joueur avance
    // a son rythme apres avoir lu les reponses du round reveal.
    setCurrentRoundOrder((prev) => prev + 1);
  }, []);

  return {
    session,
    round,
    rounds,
    currentRoundOrder,
    totalRounds,
    hasFinishedLocally,
    loading,
    error,
    connectionStatus,
    startSession,
    submitAnswer,
    nextRound,
  };
}
